import { t, TFunction } from 'i18next';
import KPI, { KPITypes } from 'models/kpi/kpi';
import { KPI_Translation } from 'models/kpi/kpi_Translation';
import Language from 'models/language';
import { PackageTenantStates } from 'models/package';
import UserLanguage from 'models/userLanguage';
import { i18nBase } from 'services/Localization/i18n';
import { Integration } from './integration';
import { IntegrationChecks } from './integrationCheckHandler';
import { IntegrationConnectionTypeHandler, IntegrationConnectionTypes } from './integrationConnectionHandler';
import { IIntegrationEvidence } from './integrationEvidence';
import { IntegrationType } from './integrationType';
import { parse_Evidence_MS_Azure } from 'components/Integrations/Groups/MS_Azure/IntegrationConfig_MS_Azure';
import { parse_Evidence_MS_AppRegistrationRestricted } from 'components/Integrations/Groups/MS_EntraBaseline/IntegrationConfig_MS_AppRegistrationRestricted';
import { parse_Evidence_MS_CAAdminMFA } from 'components/Integrations/Groups/MS_EntraBaseline/IntegrationConfig_MS_CAAdminMFA';
import { parse_Evidence_MS_AdminPortalRestriction } from 'components/Integrations/Groups/MS_EntraBaseline/IntegrationConfig_MS_AdminPortalRestriction';
import { parse_Evidence_MS_GuestInviteRestriction } from 'components/Integrations/Groups/MS_EntraBaseline/IntegrationConfig_MS_GuestInviteRestriction';
import { parse_Evidence_MS_GuestAccessRestriction } from 'components/Integrations/Groups/MS_EntraBaseline/IntegrationConfig_MS_GuestAccessRestriction';
import { parse_Evidence_MS_MFA, parse_Evidence_SslLabs } from './integrationTypeEvidenceParser';

export enum IntegrationTypes {
  NotSet = 0,
  MS_MFA = 1,
  MS_SecureScoresM365 = 2,
  MS_SecureScoresIdentity = 3,
  MS_SecureScoresDevice = 5,
  MS_SecureScoresDeviceExposure = 6,
  SslLabs = 7,
  //a check SkillControl runs (phase 20R2): an integration of the organization whose results are pushed; created through
  //its own creator and read-only on the annual plan, so getIntegrationTypes does not list it (getExternalCheckTypeHandler)
  SkillControlCheck = 8,
  //the Microsoft Entra ID baseline family (API Docs/MS_EntraBaseline.md): tenant settings read through Graph
  MS_CAAdminMFA = 9,
  MS_AdminPortalRestriction = 10,
  MS_AppRegistrationRestricted = 11,
  MS_GuestInviteRestriction = 12,
  MS_GuestAccessRestriction = 13,
  //the Microsoft Azure family (API Docs/MS_Azure.md): settings of Azure resources read through Azure Resource Manager
  MS_AzureDatabases = 18,
  MS_AzureKeyVaults = 19,
  MS_AzureLogs = 20,
  MS_AzureSecrets = 21,
}

export enum IntegrationMinimumSchedule {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export class IntegrationTypeHandler {
  integrationTypeId: IntegrationTypes;

  name: string;

  description: string;

  image: string;

  hasConfig: boolean;

  autoStartTestRunOnOpen: boolean;

  validateConfigBeforeTestRun: boolean;

  minimumSchedule: IntegrationMinimumSchedule;

  testRunConfirmationText?: string;

  //the authorization this type needs, resolved from the API row by apiGetIntegrationTypes; it owns the client id,
  //the auth mode and the custom auth button texts
  connectionType?: IntegrationConnectionTypeHandler;

  kpiDef: KPI;

  integrationType?: IntegrationType; //from back-end

  integration?: Integration; //from back-end - active integration of customer

  constructor() {
    this.integrationTypeId = IntegrationTypes.NotSet;
    this.name = '';
    this.description = '';
    this.image = '';
    this.hasConfig = true;
    this.autoStartTestRunOnOpen = true;
    this.validateConfigBeforeTestRun = false;
    this.minimumSchedule = IntegrationMinimumSchedule.Daily;
    this.kpiDef = new KPI();
  }

  getScopes(): string[] {
    return this.connectionType?.getScopes() ?? [];
  }

  //The external check handler has no kpiDef: its KPI's type is the check's kind, so the integration's own KPI decides.
  //A built-in type keeps its kpiDef: the config modal reads this for a new integration whose KPI is not set yet.
  isTemplateSupported(): boolean {
    if (this.integrationTypeId === IntegrationTypes.SkillControlCheck) {
      return this.integration?.kpi?.type === KPITypes.SuccesError;
    }

    return this.kpiDef.type === KPITypes.SuccesError;
  }

  //Keyed on the API row, so a connection type this build does not know yet still requires authorization.
  needsAuth(): boolean {
    return this.getIntegrationConnectionTypeId() !== IntegrationConnectionTypes.None;
  }

  usesAdminConsent(): boolean {
    return this.connectionType?.usesAdminConsent() ?? false;
  }

  usesCustomAuth(): boolean {
    return this.connectionType?.usesCustomAuth() ?? false;
  }

  //The connection type is a catalogue fact on the API type row, like the group and the check.
  getIntegrationConnectionTypeId(): IntegrationConnectionTypes {
    return this.integrationType?.integrationConnectionTypeId ?? IntegrationConnectionTypes.None;
  }

  //The check is a catalogue fact on the API type row, like the group; it is not set on the handler.
  getIntegrationCheckId(): IntegrationChecks {
    return this.integrationType?.integrationCheckId ?? IntegrationChecks.NotSet;
  }

  clone(): IntegrationTypeHandler {
    const clone = new IntegrationTypeHandler();
    clone.integrationTypeId = this.integrationTypeId;
    clone.name = this.name;
    clone.description = this.description;
    clone.image = this.image;
    clone.hasConfig = this.hasConfig;
    clone.autoStartTestRunOnOpen = this.autoStartTestRunOnOpen;
    clone.validateConfigBeforeTestRun = this.validateConfigBeforeTestRun;
    clone.minimumSchedule = this.minimumSchedule;
    clone.testRunConfirmationText = this.testRunConfirmationText;
    clone.connectionType = this.connectionType; //static catalogue data
    clone.integrationType = this.integrationType; //shallow copy since it's static data from back-end
    clone.integration = this.integration?.clone();
    clone.kpiDef = this.kpiDef.clone();
    clone.hasConfig = this.hasConfig;

    return clone;
  }

  isValid(): boolean {
    if (!this.integration) return false;
    if (this.needsAuth() && !this.integration.isConnectionAuthorized()) return false;
    if (!this.integration.recurrencePattern?.isActive) return false;
    if (this.isTemplateSupported() && !this.integration.templateId) return false;

    return true;
  }

  getPackageState(): PackageTenantStates {
    if (!this.integration) return PackageTenantStates.Available;

    return PackageTenantStates.Activated;
  }

  parseEvidence(rawEvidence: string): IIntegrationEvidence | undefined {
    switch (this.integrationTypeId) {
      case IntegrationTypes.MS_MFA:
        return parse_Evidence_MS_MFA(rawEvidence);
      case IntegrationTypes.SslLabs:
        return parse_Evidence_SslLabs(rawEvidence);
      case IntegrationTypes.MS_CAAdminMFA:
        return parse_Evidence_MS_CAAdminMFA(rawEvidence);
      case IntegrationTypes.MS_AdminPortalRestriction:
        return parse_Evidence_MS_AdminPortalRestriction(rawEvidence);
      case IntegrationTypes.MS_AppRegistrationRestricted:
        return parse_Evidence_MS_AppRegistrationRestricted(rawEvidence);
      case IntegrationTypes.MS_GuestInviteRestriction:
        return parse_Evidence_MS_GuestInviteRestriction(rawEvidence);
      case IntegrationTypes.MS_GuestAccessRestriction:
        return parse_Evidence_MS_GuestAccessRestriction(rawEvidence);
      //one evidence shape for the four Azure checks
      case IntegrationTypes.MS_AzureDatabases:
      case IntegrationTypes.MS_AzureKeyVaults:
      case IntegrationTypes.MS_AzureLogs:
      case IntegrationTypes.MS_AzureSecrets:
        return parse_Evidence_MS_Azure(rawEvidence);
      default:
        throw new Error(`Parsing evidence for integration type ${this.integrationTypeId} is not implemented`);
    }
  }
}

function getKPITranslations(languages: string[], nameKey: string, descriptionKey: string) {
  //Return translations for each available language.
  const trans: KPI_Translation[] = [];

  languages.forEach((key) => {
    const newTrans = new KPI_Translation();
    newTrans.lang = new Language();
    newTrans.lang.code = key;
    newTrans.name = t(nameKey, { lng: key });
    newTrans.description = t(descriptionKey, { lng: key });
    trans.push(newTrans);
  });

  return trans;
}

export async function getIntegrationTypeKPI(integrationType: IntegrationTypeHandler): Promise<KPI> {
  //load all languages to ensure translations are available
  const languages = UserLanguage.getSupportedLanguages();
  const langs = Array.from(languages.keys());
  await i18nBase.loadLanguages(langs);

  //Fill in the props & translations
  const kpiDef = integrationType.kpiDef.clone();
  const keyName = IntegrationTypes[integrationType.integrationTypeId];
  kpiDef.trans = getKPITranslations(langs, `integrations:${keyName}.Name`, `integrations:${keyName}.Description`);

  return kpiDef;
}

export function getIntegrationTypes(t: TFunction<string[]>) {
  const integrationTypes: IntegrationTypeHandler[] = [];

  const itp_MS_MFA = new IntegrationTypeHandler();
  itp_MS_MFA.integrationTypeId = IntegrationTypes.MS_MFA;
  itp_MS_MFA.name = t('integrations:MS_MFA.Name');
  itp_MS_MFA.description = t('integrations:MS_MFA.Description');
  itp_MS_MFA.image = 'ms_mfa.png';
  itp_MS_MFA.hasConfig = true;
  itp_MS_MFA.kpiDef.type = KPITypes.SuccesError;
  itp_MS_MFA.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_MFA);

  const itp_MS_SecureScoresM365 = new IntegrationTypeHandler();
  itp_MS_SecureScoresM365.integrationTypeId = IntegrationTypes.MS_SecureScoresM365;
  itp_MS_SecureScoresM365.name = t('integrations:MS_SecureScoresM365.Name');
  itp_MS_SecureScoresM365.description = t('integrations:MS_SecureScoresM365.Description');
  itp_MS_SecureScoresM365.image = 'ms_securescores_m365.png';
  itp_MS_SecureScoresM365.hasConfig = false;
  itp_MS_SecureScoresM365.kpiDef.type = KPITypes.Number;
  itp_MS_SecureScoresM365.kpiDef.showLabel = false;
  itp_MS_SecureScoresM365.kpiDef.decimalCount = 2;
  integrationTypes.push(itp_MS_SecureScoresM365);

  const itp_MS_SecureScoresIdentity = new IntegrationTypeHandler();
  itp_MS_SecureScoresIdentity.integrationTypeId = IntegrationTypes.MS_SecureScoresIdentity;
  itp_MS_SecureScoresIdentity.name = t('integrations:MS_SecureScoresIdentity.Name');
  itp_MS_SecureScoresIdentity.description = t('integrations:MS_SecureScoresIdentity.Description');
  itp_MS_SecureScoresIdentity.image = 'ms_securescores_identity.png';
  itp_MS_SecureScoresIdentity.hasConfig = false;
  itp_MS_SecureScoresIdentity.kpiDef.type = KPITypes.Number;
  itp_MS_SecureScoresIdentity.kpiDef.showLabel = false;
  itp_MS_SecureScoresIdentity.kpiDef.decimalCount = 2;
  integrationTypes.push(itp_MS_SecureScoresIdentity);

  const itp_MS_SecureScoresDevice = new IntegrationTypeHandler();
  itp_MS_SecureScoresDevice.integrationTypeId = IntegrationTypes.MS_SecureScoresDevice;
  itp_MS_SecureScoresDevice.name = t('integrations:MS_SecureScoresDevice.Name');
  itp_MS_SecureScoresDevice.description = t('integrations:MS_SecureScoresDevice.Description');
  itp_MS_SecureScoresDevice.image = 'ms_securescores_device.png';
  itp_MS_SecureScoresDevice.hasConfig = false;
  itp_MS_SecureScoresDevice.kpiDef.type = KPITypes.Number;
  itp_MS_SecureScoresDevice.kpiDef.showLabel = false;
  itp_MS_SecureScoresDevice.kpiDef.decimalCount = 2;
  integrationTypes.push(itp_MS_SecureScoresDevice);

  const itp_MS_SecureScoresDeviceExposure = new IntegrationTypeHandler();
  itp_MS_SecureScoresDeviceExposure.integrationTypeId = IntegrationTypes.MS_SecureScoresDeviceExposure;
  itp_MS_SecureScoresDeviceExposure.name = t('integrations:MS_SecureScoresDeviceExposure.Name');
  itp_MS_SecureScoresDeviceExposure.description = t('integrations:MS_SecureScoresDeviceExposure.Description');
  itp_MS_SecureScoresDeviceExposure.image = 'ms_securescores_device_exposure.png';
  itp_MS_SecureScoresDeviceExposure.hasConfig = false;
  itp_MS_SecureScoresDeviceExposure.kpiDef.type = KPITypes.Number;
  itp_MS_SecureScoresDeviceExposure.kpiDef.showLabel = false;
  itp_MS_SecureScoresDeviceExposure.kpiDef.decimalCount = 2;
  integrationTypes.push(itp_MS_SecureScoresDeviceExposure);

  const itp_SslLabs = new IntegrationTypeHandler();
  itp_SslLabs.integrationTypeId = IntegrationTypes.SslLabs;
  itp_SslLabs.name = t('integrations:SslLabs.Name');
  itp_SslLabs.description = t('integrations:SslLabs.Description');
  itp_SslLabs.image = 'ssl_labs.png';
  itp_SslLabs.hasConfig = true;
  itp_SslLabs.autoStartTestRunOnOpen = false;
  itp_SslLabs.validateConfigBeforeTestRun = true;
  itp_SslLabs.minimumSchedule = IntegrationMinimumSchedule.Weekly;
  itp_SslLabs.testRunConfirmationText = t('integrations:SslLabs.TestRunConfirmationText');
  itp_SslLabs.kpiDef.type = KPITypes.SuccesError;
  itp_SslLabs.kpiDef.showLabel = true;
  integrationTypes.push(itp_SslLabs);

  const itp_MS_CAAdminMFA = new IntegrationTypeHandler();
  itp_MS_CAAdminMFA.integrationTypeId = IntegrationTypes.MS_CAAdminMFA;
  itp_MS_CAAdminMFA.name = t('integrations:MS_CAAdminMFA.Name');
  itp_MS_CAAdminMFA.description = t('integrations:MS_CAAdminMFA.Description');
  itp_MS_CAAdminMFA.image = 'ms_entra_baseline.png';
  itp_MS_CAAdminMFA.hasConfig = true;
  itp_MS_CAAdminMFA.kpiDef.type = KPITypes.SuccesError;
  itp_MS_CAAdminMFA.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_CAAdminMFA);

  const itp_MS_AdminPortalRestriction = new IntegrationTypeHandler();
  itp_MS_AdminPortalRestriction.integrationTypeId = IntegrationTypes.MS_AdminPortalRestriction;
  itp_MS_AdminPortalRestriction.name = t('integrations:MS_AdminPortalRestriction.Name');
  itp_MS_AdminPortalRestriction.description = t('integrations:MS_AdminPortalRestriction.Description');
  itp_MS_AdminPortalRestriction.image = 'ms_entra_baseline.png';
  itp_MS_AdminPortalRestriction.hasConfig = true;
  itp_MS_AdminPortalRestriction.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AdminPortalRestriction.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AdminPortalRestriction);

  const itp_MS_AppRegistrationRestricted = new IntegrationTypeHandler();
  itp_MS_AppRegistrationRestricted.integrationTypeId = IntegrationTypes.MS_AppRegistrationRestricted;
  itp_MS_AppRegistrationRestricted.name = t('integrations:MS_AppRegistrationRestricted.Name');
  itp_MS_AppRegistrationRestricted.description = t('integrations:MS_AppRegistrationRestricted.Description');
  itp_MS_AppRegistrationRestricted.image = 'ms_entra_baseline.png';
  itp_MS_AppRegistrationRestricted.hasConfig = true;
  itp_MS_AppRegistrationRestricted.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AppRegistrationRestricted.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AppRegistrationRestricted);

  const itp_MS_GuestInviteRestriction = new IntegrationTypeHandler();
  itp_MS_GuestInviteRestriction.integrationTypeId = IntegrationTypes.MS_GuestInviteRestriction;
  itp_MS_GuestInviteRestriction.name = t('integrations:MS_GuestInviteRestriction.Name');
  itp_MS_GuestInviteRestriction.description = t('integrations:MS_GuestInviteRestriction.Description');
  itp_MS_GuestInviteRestriction.image = 'ms_entra_baseline.png';
  itp_MS_GuestInviteRestriction.hasConfig = true;
  itp_MS_GuestInviteRestriction.kpiDef.type = KPITypes.SuccesError;
  itp_MS_GuestInviteRestriction.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_GuestInviteRestriction);
  const itp_MS_GuestAccessRestriction = new IntegrationTypeHandler();
  itp_MS_GuestAccessRestriction.integrationTypeId = IntegrationTypes.MS_GuestAccessRestriction;
  itp_MS_GuestAccessRestriction.name = t('integrations:MS_GuestAccessRestriction.Name');
  itp_MS_GuestAccessRestriction.description = t('integrations:MS_GuestAccessRestriction.Description');
  itp_MS_GuestAccessRestriction.image = 'ms_entra_baseline.png';
  itp_MS_GuestAccessRestriction.hasConfig = true;
  itp_MS_GuestAccessRestriction.kpiDef.type = KPITypes.SuccesError;
  itp_MS_GuestAccessRestriction.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_GuestAccessRestriction);
  //The four Azure checks share one connection and one evidence shape. Their config holds dated acceptances and one
  //option, which the API validates (validate-config), so a test run validates the config first.
  const itp_MS_AzureDatabases = new IntegrationTypeHandler();
  itp_MS_AzureDatabases.integrationTypeId = IntegrationTypes.MS_AzureDatabases;
  itp_MS_AzureDatabases.name = t('integrations:MS_AzureDatabases.Name');
  itp_MS_AzureDatabases.description = t('integrations:MS_AzureDatabases.Description');
  itp_MS_AzureDatabases.image = 'ms_azure.png';
  itp_MS_AzureDatabases.hasConfig = true;
  itp_MS_AzureDatabases.validateConfigBeforeTestRun = true;
  itp_MS_AzureDatabases.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AzureDatabases.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AzureDatabases);

  const itp_MS_AzureKeyVaults = new IntegrationTypeHandler();
  itp_MS_AzureKeyVaults.integrationTypeId = IntegrationTypes.MS_AzureKeyVaults;
  itp_MS_AzureKeyVaults.name = t('integrations:MS_AzureKeyVaults.Name');
  itp_MS_AzureKeyVaults.description = t('integrations:MS_AzureKeyVaults.Description');
  itp_MS_AzureKeyVaults.image = 'ms_azure.png';
  itp_MS_AzureKeyVaults.hasConfig = true;
  itp_MS_AzureKeyVaults.validateConfigBeforeTestRun = true;
  itp_MS_AzureKeyVaults.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AzureKeyVaults.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AzureKeyVaults);

  const itp_MS_AzureLogs = new IntegrationTypeHandler();
  itp_MS_AzureLogs.integrationTypeId = IntegrationTypes.MS_AzureLogs;
  itp_MS_AzureLogs.name = t('integrations:MS_AzureLogs.Name');
  itp_MS_AzureLogs.description = t('integrations:MS_AzureLogs.Description');
  itp_MS_AzureLogs.image = 'ms_azure.png';
  itp_MS_AzureLogs.hasConfig = true;
  itp_MS_AzureLogs.validateConfigBeforeTestRun = true;
  itp_MS_AzureLogs.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AzureLogs.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AzureLogs);

  const itp_MS_AzureSecrets = new IntegrationTypeHandler();
  itp_MS_AzureSecrets.integrationTypeId = IntegrationTypes.MS_AzureSecrets;
  itp_MS_AzureSecrets.name = t('integrations:MS_AzureSecrets.Name');
  itp_MS_AzureSecrets.description = t('integrations:MS_AzureSecrets.Description');
  itp_MS_AzureSecrets.image = 'ms_azure.png';
  itp_MS_AzureSecrets.hasConfig = true;
  itp_MS_AzureSecrets.validateConfigBeforeTestRun = true;
  itp_MS_AzureSecrets.kpiDef.type = KPITypes.SuccesError;
  itp_MS_AzureSecrets.kpiDef.showLabel = true;
  integrationTypes.push(itp_MS_AzureSecrets);

  return integrationTypes;
}

//The handler of a SkillControl check: its name and description for the list, the filter and the sort. Added next to
//getIntegrationTypes only where an integration the API returns is shown (the schedule scenes, the KPI data tab); the
//integration catalogue never offers it.
export function getExternalCheckTypeHandler(t: TFunction<string[]>) {
  const itp_ExternalCheck = new IntegrationTypeHandler();
  itp_ExternalCheck.integrationTypeId = IntegrationTypes.SkillControlCheck;
  itp_ExternalCheck.name = t('integrations:ExternalCheck.Name');
  itp_ExternalCheck.description = t('integrations:ExternalCheck.Description');
  itp_ExternalCheck.hasConfig = false;
  itp_ExternalCheck.autoStartTestRunOnOpen = false;

  return itp_ExternalCheck;
}

//Every handler an integration the API returns may need: the catalogue plus the SkillControl check. For resolving a
//handler by integrationTypeId, never for offering types in the store (that is getIntegrationTypes).
export function getIntegrationTypeHandlersForDisplay(t: TFunction<string[]>): IntegrationTypeHandler[] {
  return [...getIntegrationTypes(t), getExternalCheckTypeHandler(t)];
}
